import React, { useState, useEffect, useMemo, useRef, useCallback, memo } from 'react';
import { getSupabaseClient } from '../supabaseClient';
import { useBusinessType } from '../context/BusinessContext';
import { useMediaQuery } from '../hooks/use-media-query';
import { API_BASE_URL, endpoints } from '../config/api';
import { 
  ChartBar, 
  WarningCircle, 
  CircleNotch, 
  Chat, 
  CheckCircle, 
  XCircle, 
  ArrowSquareOut, 
  Question, 
  Gear, 
  CaretDown, 
  CaretRight, 
  Stack, 
  Lightning, 
  ChatCircle, 
  PaperPlaneTilt, 
  Calendar, 
  CurrencyDollar, 
  FileText, 
  LinkSimple, 
  PencilSimple, 
  Plus, 
  DotsThreeVertical,
  BookOpen,
  Database,
  Wrench,
  GitBranch,
  Envelope,
  PhoneTransfer,
  Code,
  Note,
  Hash,
  X,
  Check,
  Info,
  Funnel,
  User,
  Package,
  Truck,
  Buildings,
  ShirtFolded,
  GridFour,
  Globe,
  Sparkle,
  ArrowsClockwise
} from '@phosphor-icons/react';
import { Phone, MoreVertical } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from "sonner";
import CallDetailsModal from '../components/modals/CallDetailsModal';
import SimpleAnswerModal from '../brain/SimpleAnswerModal';
import { ComplexAnswerSettingsModal } from '../components/complex-answer/ComplexAnswerSettingsModal';
import CallActionSettingsModal from '../components/call-actions/CallActionSettingsModal';
import NewCategoryModal from '../brain/NewCategoryModal';
import BrainActionModal from '../brain/BrainActionModal';
import UpdateExistingModal from '../components/modals/UpdateExistingModal';



// Simplified animation variants for better performance
const pageVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.3 } },
  exit: { opacity: 0, transition: { duration: 0.2 } }
};

const cardVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.2 } }
};

// Simplified content variants
const contentVariants = {
  collapsed: { 
    opacity: 0,
    height: 0,
    transition: { duration: 0.2 }
  },
  expanded: { 
    opacity: 1,
    height: "auto",
    transition: { duration: 0.2 }
  }
};

// Memoized helper functions
const getTypeConfig = memo((type, action_type = null) => {
  const baseTypes = {
    'simple_answer': {
      icon: BookOpen, 
      color: 'bg-blue-100 text-blue-700',
      title: 'Simple Answer'
    },
    'complex_answer': { 
      icon: Database, 
      color: 'bg-purple-100 text-purple-700',
      title: 'Complex Answer'
    },
    'simple_action': {
      icon: Wrench, 
      color: 'bg-amber-100 text-amber-700',
      title: 'Simple Action'
    },
    'complex_action': { 
      icon: GitBranch, 
      color: 'bg-green-100 text-green-700',
      title: 'Complex Action'
    },
    'default': { 
      icon: BookOpen,
      color: 'bg-gray-100 text-gray-700',
      title: 'Unknown Type'
    }
  };

  if (type === 'simple_action' && action_type) {
    const simpleActionBaseConfig = baseTypes.simple_action;
    let specificIcon = simpleActionBaseConfig.icon;
    let specificTitle = simpleActionBaseConfig.title;

    switch (action_type) {
      case 'sms':
        specificIcon = Chat;
        specificTitle = 'SMS Action';
        break;
      case 'email':
        specificIcon = Envelope;
        specificTitle = 'Email Action';
        break;
      case 'transfer':
        specificIcon = PhoneTransfer;
        specificTitle = 'Call Transfer';
        break;
      case 'api':
        specificIcon = Code;
        specificTitle = 'API Action';
        break;
      case 'note':
        specificIcon = Note;
        specificTitle = 'Note Action';
        break;
      case 'dtmf':
        specificIcon = Hash;
        specificTitle = 'DTMF Action';
        break;
    }
    return { icon: specificIcon, color: simpleActionBaseConfig.color, title: specificTitle };
  }
  return baseTypes[type] || baseTypes.default;
});

const getActionTypeIcon = (actionType) => {
  switch (actionType) {
    case 'api':
      return LinkSimple;
    case 'phone':
      return Phone;
    case 'email':
      return PaperPlaneTilt;
    case 'calendar':
      return Calendar;
    case 'payment':
      return CurrencyDollar;
    default:
      return Lightning;
  }
};

const getBrainSettingDisplay = (settingType, actionType) => {
  switch (settingType) {
    case 'simple_action':
      const ActionIcon = getActionTypeIcon(actionType);
      return {
        icon: ActionIcon,
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-700',
        label: actionType || 'action'
      };
    case 'simple_answer':
      return {
        icon: ChatCircle,
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
        label: 'answer'
      };
    case 'complex_answer':
      return {
        icon: Database,
        bgColor: 'bg-purple-100',
        textColor: 'text-purple-700',
        label: 'complex'
      };
    default:
      return {
        icon: Gear,
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-600',
        label: 'setting'
      };
  }
};

// Dynamic color helper for progress bars based on coverage percentage
const getCoverageColors = (percentage) => {
  if (percentage === 0) {
    // Crisis Level: 0% coverage - hazmat-style striped pattern for maximum urgency
    return {
      gradient: 'bg-red-400', // Fallback solid color for accessibility
      height: 'h-1.5',
      background: 'bg-red-100',
      isStripes: true,
      stripePattern: 'repeating-linear-gradient(45deg, #ef4444 0px, #ef4444 8px, #fecaca 8px, #fecaca 16px)'
    };
  } else if (percentage >= 80) {
    // Green: Well-performing (80%+)
    return {
      gradient: 'bg-gradient-to-r from-green-400 to-green-500',
      height: 'h-1.5',
      background: 'bg-gray-200',
      isStripes: false
    };
  } else if (percentage >= 30) {
    // Yellow: Needs improvement (30-79%)
    return {
      gradient: 'bg-gradient-to-r from-yellow-400 to-yellow-500',
      height: 'h-1.5',
      background: 'bg-gray-200',
      isStripes: false
    };
  } else {
    // Red: Critical issues (1-29%) - light red background for urgency
    return {
      gradient: 'bg-gradient-to-r from-red-400 to-red-500',
      height: 'h-1.5',
      background: 'bg-red-100',
      isStripes: false
    };
  }
};

// Helper function to format date in readable format
const formatLastUpdated = (date) => {
  if (!date) return null;

  const options = { 
    month: 'long', 
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  };

  const formatted = date.toLocaleDateString('en-US', options);
  // Convert "May 30, 2025 at 8:30 PM" to "May 30th at 8:30 pm"
  return formatted
    .replace(/(\d+),/, '$1th') // Add 'th' after day number
    .replace(' at ', ' at ')
    .replace('AM', 'am')
    .replace('PM', 'pm');
};

// Function to get Steve Jobs-style inspiring messages based on coverage percentage
const getCoverageMessage = (percentage, selectedServiceType = null) => {
  const serviceConfig = selectedServiceType ? getServiceTypeConfig(selectedServiceType) : null;
  const serviceContext = serviceConfig ? ` on ${serviceConfig.label.toLowerCase()}` : '';
  
  // 0% - Special encouraging message
  if (percentage === 0) {
    return serviceConfig 
      ? `Your AI is a blank canvas, ready to learn everything about ${serviceConfig.label.toLowerCase()}.`
      : "Your AI is a blank canvas, ready to learn everything about your business.";
  }
  
  // 1-19% - Red, early stage
  if (percentage < 20) {
    const messages = [
      `Every expert was once a beginner. Your AI is taking its first steps${serviceContext}.`,
      `Your AI is starting to understand your business${serviceContext}. Keep adding training.`,
      `Great start! Your AI is building its foundation of knowledge${serviceContext}.`,
      `Your AI is learning the basics${serviceContext}. Each setting makes it smarter.`
    ];
    return messages[Math.floor(percentage / 5) % messages.length];
  }
  
  // 20-79% - Yellow, growing stage
  if (percentage < 80) {
    const messages = [
      `Your AI is getting smarter with every setting you add${serviceContext}.`, // 20-29%
      `Excellent progress! Your AI is becoming more knowledgeable${serviceContext}.`, // 30-39%
      `Your AI is learning fast and gaining confidence${serviceContext}.`, // 40-49%
      `Impressive! Your AI is developing deep understanding${serviceContext}.`, // 50-59%
      `Your AI is becoming quite capable${serviceContext}. Keep building its knowledge.`, // 60-69%
      `Outstanding work! Your AI is nearly ready for anything customers ask${serviceContext}.` // 70-79%
    ];
    const messageIndex = Math.floor((percentage - 20) / 10);
    return messages[Math.min(messageIndex, messages.length - 1)];
  }
  
  // 80%+ - Green, mastery stage
  const messages = [
    `Your AI is well-trained${serviceContext} and ready to help customers brilliantly.`, // 80-89%
    `Exceptional! Your AI has mastered your business knowledge${serviceContext}.`, // 90-99%
    `Perfect! Your AI is fully trained${serviceContext} and ready for anything.` // 100%
  ];
  const messageIndex = Math.min(Math.floor((percentage - 80) / 10), messages.length - 1);
  return messages[messageIndex];
};

const getStatDescription = (statType, isTemplate, coveragePercentage = 0, selectedServiceType = null) => {
  const serviceConfig = selectedServiceType ? getServiceTypeConfig(selectedServiceType) : null;
  const serviceName = serviceConfig ? serviceConfig.label.toLowerCase() : null;

  if (!isTemplate) {
    switch (statType) {
      case 'total':
        return "Every question we've analyzed.";
      case 'covered':
        return serviceName ? `Questions our AI can handle about ${serviceName}.` : "Questions our AI can handle.";
      case 'coverage':
        return getCoverageMessage(coveragePercentage, selectedServiceType);
      default:
        return "";
    }
  }

  switch (statType) {
    case 'total':
      return "Common questions customers ask.";
    case 'covered':
      return serviceName ? `Questions your AI can handle about ${serviceName}.` : "Questions your AI can handle.";
    case 'coverage':
      return getCoverageMessage(coveragePercentage, selectedServiceType);
    default:
      return "";
  }
};

// Memoized StatCard component - Mobile optimized
const StatCard = memo(({ title, value, icon: Icon, description, isLoading, showProgressBar = false, percentage = null }) => (
  <div className="bg-white p-4 sm:p-5 rounded-xl shadow-sm border border-gray-100">
    <div className="flex items-center text-gray-500 mb-2">
      <Icon className="w-4 h-4 mr-2 flex-shrink-0" />
      <h3 className="text-xs sm:text-sm font-medium truncate">{title}</h3>
    </div>
    {isLoading ? (
      <div className="h-6 sm:h-8 bg-gray-200 rounded animate-pulse w-1/2 my-1"></div>
    ) : (
      <div className="flex items-center space-x-2 sm:space-x-3">
        <p className="text-xl sm:text-2xl font-semibold text-gray-800 truncate">{value}</p>
        {showProgressBar && percentage !== null && (
          <div className={`flex-1 ${getCoverageColors(percentage).background} rounded-full h-2 min-w-[40px] sm:min-w-[60px]`}>
            {getCoverageColors(percentage).isStripes ? (
              // Crisis level: 0% - show striped pattern filling entire bar
              <div 
                className="w-full h-2 rounded-full"
                style={{ 
                  background: getCoverageColors(percentage).stripePattern,
                  transition: 'all 0.3s ease'
                }}
              />
            ) : (
              // Normal progress bar
              <div 
                className={`${getCoverageColors(percentage).gradient} ${getCoverageColors(percentage).height} rounded-full transition-all duration-300`}
                style={{ width: `${percentage}%` }}
              />
            )}
          </div>
        )}
      </div>
    )}
    {description && !isLoading && (
      <p className="text-xs text-gray-400 mt-1 leading-relaxed">{description}</p>
    )}
  </div>
));

// Optimized dropdown component
const UncoveredActionDropdown = memo(({ inquiry, onCreateNew, onUpdateExisting, availableSettings }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });

  const handleToggle = useCallback((e) => {
    e.stopPropagation();

    if (!isOpen) {
      const rect = e.currentTarget.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        right: window.innerWidth - rect.right - window.scrollX
      });
    }

    setIsOpen(!isOpen);
  }, [isOpen]);

  const handleCreateNew = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen(false);
    onCreateNew(inquiry);
  }, [inquiry, onCreateNew]);

  return (
    <>
      <button
        onClick={handleToggle}
        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
        title="Actions for this question"
      >
        <MoreVertical className="w-3.5 h-3.5" />
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
          />

          <div
            className="absolute w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
            style={{
              top: `${dropdownPosition.top}px`,
              right: `${dropdownPosition.right}px`
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="py-1">
              <button
                onClick={handleCreateNew}
                onMouseDown={(e) => e.stopPropagation()}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center relative z-10"
              >
                <Plus className="w-4 h-4 mr-2 text-green-500" />
                Create New Setting
              </button>

              {availableSettings.length > 0 && (
                <>
                  <div className="border-t border-gray-100 my-1" />
                  <div className="px-3 py-1">
                    <p className="text-xs text-gray-500 font-medium">Update Existing:</p>
                  </div>
                  <div className="max-h-32 overflow-y-auto">
                    {availableSettings.map(setting => {
                      const display = getBrainSettingDisplay(setting.type, setting.action_type);
                      return (
                        <button
                          key={setting.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setIsOpen(false);
                            onUpdateExisting(inquiry, setting);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center relative z-10"
                        >
                          <display.icon className={`w-3 h-3 mr-2 ${display.textColor} flex-shrink-0`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-1">
                              {setting.brain_category_name && (
                                <span className="text-gray-500 text-xs">{setting.brain_category_name}</span>
                              )}
                              {setting.brain_category_name && setting.name && (
                                <span className="text-gray-400 text-xs">•</span>
                              )}
                              <span className="truncate font-medium">{setting.name}</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
});

// Optimized InquiryItem component with memoization
const InquiryItem = memo(({ inquiry, onViewCallDetails, onEditSetting, onCreateSetting, onUpdateExisting, availableSettings, isTemplateMode = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showUpdateExistingModal, setShowUpdateExistingModal] = useState(false);

  const handleItemClick = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const handleEditClick = useCallback((e) => {
    e.stopPropagation();
    if (onEditSetting && inquiry.brain_setting_id) {
      onEditSetting(inquiry);
    }
  }, [inquiry, onEditSetting]);

  const handleViewCallClick = useCallback((e) => {
    e.stopPropagation();
    if (inquiry.call_id && onViewCallDetails) {
      onViewCallDetails(inquiry.call_id);
    }
  }, [inquiry.call_id, onViewCallDetails]);

  // Memoized icon configuration
  const iconConfig = useMemo(() => {
    if (inquiry.covered) {
      return {
        icon: Check,
        color: 'bg-green-100 text-green-700'
      };
    } else {
      return {
        icon: X,
        color: 'bg-red-100 text-red-700'
      };
    }
  }, [inquiry.covered]);

  const IconComponent = iconConfig.icon;

  // Memoized date/priority formatting
  const formattedDateOrPriority = useMemo(() => {
    if (isTemplateMode) {
      // Just return the priority value, not "Priority: value"
      return inquiry.priority?.charAt(0).toUpperCase() + inquiry.priority?.slice(1);
    }

    const callDate = new Date(inquiry.call_created_at || inquiry.created_at);
    const currentYear = new Date().getFullYear();
    const callYear = callDate.getFullYear();

    const options = { 
      month: 'long', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      ...(callYear !== currentYear && { year: 'numeric' })
    };

    const formatted = callDate.toLocaleDateString('en-US', options);
    // Convert "May 30, 2025 at 8:30 PM" to "May 30 at 8:30 pm"
    return formatted
      .replace(/(\d+),/, '$1') // Remove comma after day
      .replace(' at ', ' at ')
      .replace('AM', 'am')
      .replace('PM', 'pm');
  }, [inquiry, isTemplateMode]);

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-100 shadow-sm relative cursor-pointer hover:bg-gray-50">
        {/* Main Content */}
        <div className="p-3 sm:p-4" onClick={handleItemClick}>
          <div className="flex items-center">
            {/* Left: Status Icon */}
            <div className={`p-2 rounded-lg ${iconConfig.color} mr-3 flex-shrink-0 flex items-center justify-center`}>
              <IconComponent size={20} />
            </div>

            {/* Middle: Inquiry Text Only */}
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-medium text-gray-800">
                {inquiry.inquiry}
              </h4>
            </div>
          </div>
        </div>

        {/* Expandable Details */}
        {isExpanded && (
          <div className="overflow-hidden border-t border-gray-100 bg-gray-50/50">
            <div className="px-3 sm:px-4 py-3">
              <div className="space-y-3 text-xs">
                {/* Date/Priority - shown for all items */}
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500 font-medium">
                    {isTemplateMode ? 'Priority:' : 'Call Date:'}
                  </span>
                  <span className="text-gray-700">{formattedDateOrPriority}</span>
                </div>

                {/* Service Type - shown for template items */}
                {isTemplateMode && inquiry.service_type && (
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-500 font-medium">Service Type:</span>
                    <span className="text-gray-700 capitalize">
                      {inquiry.service_type.replace(/_/g, ' ')}
                    </span>
                  </div>
                )}

                {/* Status - shown for all items for accessibility */}
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500 font-medium">Status:</span>
                  <span className="text-gray-700">{inquiry.covered ? 'Covered' : 'Not Covered'}</span>
                </div>

                {/* COVERED ITEMS: Show metadata + action buttons */}
                {inquiry.covered && (
                  <>
                    {/* Informational metadata */}
                    <div className="space-y-2">
                      {inquiry.brain_setting_type && (
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-500 font-medium">Type:</span>
                          <span className="text-gray-700">
                            {inquiry.brain_setting_type === 'simple_answer' && 'Simple Answer'}
                            {inquiry.brain_setting_type === 'complex_answer' && 'Complex Answer'}
                            {inquiry.brain_setting_type === 'simple_action' && 'Simple Action'}
                            {inquiry.brain_setting_type === 'complex_action' && 'Complex Action'}
                          </span>
                        </div>
                      )}

                      {inquiry.brain_category_name && (
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-500 font-medium">Category:</span>
                          <span className="text-gray-700">{inquiry.brain_category_name}</span>
                        </div>
                      )}

                      {inquiry.brain_setting_name && (
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-500 font-medium">Section:</span>
                          <span className="text-gray-700">{inquiry.brain_setting_name}</span>
                        </div>
                      )}
                    </div>

                    {/* Action buttons for covered items */}
                    <div className="flex flex-wrap gap-2 pt-1">
                      {/* View Call Details Button - Primary action (only for real data) */}
                      {!isTemplateMode && (
                        <button
                          onClick={handleViewCallClick}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:border-blue-300 transition-colors duration-150"
                        >
                          <ArrowSquareOut className="w-3 h-3 mr-1.5" />
                          View Call Details
                        </button>
                      )}

                      {/* Edit Section Button - Secondary action */}
                      {inquiry.brain_setting_id && (
                        <button
                          onClick={handleEditClick}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-md hover:text-yellow-700 hover:bg-yellow-50 hover:border-yellow-200 transition-colors duration-150"
                        >
                          <PencilSimple className="w-3 h-3 mr-1.5" />
                          Edit Section
                        </button>
                      )}
                    </div>
                  </>
                )}

                {/* UNCOVERED ITEMS: Show actionable buttons */}
                {!inquiry.covered && (
                  <div className="flex flex-wrap gap-2">
                    {/* View Call Details Button - Primary action (only for real data) */}
                    {!isTemplateMode && (
                      <button
                        onClick={handleViewCallClick}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:border-blue-300 transition-colors duration-150"
                      >
                        <ArrowSquareOut className="w-3 h-3 mr-1.5" />
                        View Call Details
                      </button>
                    )}

                    {/* Add New Section Button - Opens BrainActionModal */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onCreateSetting(inquiry);
                      }}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-md hover:text-green-700 hover:bg-green-50 hover:border-green-200 transition-colors duration-150"
                    >
                      <Plus className="w-3 h-3 mr-1.5" />
                      Add New Section
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Update Existing Settings Modal */}
      <UpdateExistingModal
        isOpen={showUpdateExistingModal}
        onClose={() => setShowUpdateExistingModal(false)}
        availableSettings={availableSettings}
        inquiry={inquiry}
        onSelectSetting={(setting) => {
          setShowUpdateExistingModal(false);
          onUpdateExisting(inquiry, setting);
        }}
      />
    </>
  );
});

// Optimized CategoryAccordionItem with memoization
const CategoryAccordionItem = memo(({ category, expandedItems, toggleExpand, onViewCallDetails, onEditSetting, onCreateSetting, onUpdateExisting, availableSettings, isTemplateMode = false }) => {
  const isExpanded = expandedItems.includes(category.id);

  // Memoized calculations
  const { coveredCount, totalCount, coveragePercentage } = useMemo(() => {
    const covered = category.inquiries?.filter(inquiry => inquiry.covered).length || 0;
    const total = category.inquiries?.length || 0;
    const percentage = total > 0 ? Math.round((covered / total) * 100) : 0;

    return {
      coveredCount: covered,
      totalCount: total,
      coveragePercentage: percentage
    };
  }, [category.inquiries]);

  const handleToggle = useCallback(() => {
    toggleExpand(category.id);
  }, [category.id, toggleExpand]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50/30 transition-colors duration-150"
        onClick={handleToggle}
      >
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <div
            className="text-gray-400 transition-transform duration-200"
            style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
          >
            <CaretRight className="w-5 h-5" />
          </div>
          <Stack className="w-5 h-5 text-gray-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-medium text-gray-800 truncate">
              {category.friendly_name || category.category_name}
            </h3>
            <div className="flex items-center space-x-2 mt-1">
              <span className="text-xs text-gray-500">
                {totalCount} {totalCount === 1 ? 'question' : 'questions'}
              </span>
              <div className={`w-16 ${getCoverageColors(coveragePercentage).background} rounded-full h-1.5`}>
                {getCoverageColors(coveragePercentage).isStripes ? (
                  // Crisis level: 0% - show striped pattern filling entire bar
                  <div 
                    className="w-full h-1.5 rounded-full"
                    style={{ 
                      background: getCoverageColors(coveragePercentage).stripePattern,
                      transition: 'all 0.3s ease'
                    }}
                  />
                ) : (
                  // Normal progress bar
                  <div 
                    className={`${getCoverageColors(coveragePercentage).gradient} ${getCoverageColors(coveragePercentage).height} rounded-full transition-all duration-300`}
                    style={{ width: `${coveragePercentage}%` }}
                  />
                )}
              </div>
              <span className="text-xs font-medium text-gray-600">
                {coveragePercentage}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-100 bg-gray-50/30">
          <div className="p-4 space-y-2">
            {category.inquiries && category.inquiries.length > 0 ? (
              category.inquiries.map(inquiry => (
                <InquiryItem 
                  key={inquiry.id} 
                  inquiry={inquiry}
                  onViewCallDetails={onViewCallDetails}
                  onEditSetting={onEditSetting}
                  onCreateSetting={onCreateSetting}
                  onUpdateExisting={onUpdateExisting}
                  availableSettings={availableSettings}
                  isTemplateMode={isTemplateMode}
                />
              ))
            ) : (
              <div className="text-center py-4 text-gray-500 text-sm">
                No questions in this category
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

// Update the getServiceTypeConfig function with Jobs-inspired icon choices
const getServiceTypeConfig = (serviceType) => {
  const configs = {
    'self_service': {
      icon: User,  // Clean, simple user representation
      label: 'Self Service',
      color: 'bg-slate-100 text-slate-700',
      hoverColor: 'hover:bg-slate-200',
      selectedBg: 'bg-gradient-to-br from-slate-500 to-slate-600',
      selectedBorder: 'border-slate-200',
      selectedTextColor: 'text-slate-100'
    },
    'wash_dry_fold': {
      icon: Package,    // Represents the final folded product
      label: 'Wash, Dry & Fold',
      color: 'bg-sky-100 text-sky-700',
      hoverColor: 'hover:bg-sky-200',
      selectedBg: 'bg-gradient-to-br from-sky-500 to-sky-600',
      selectedBorder: 'border-sky-200',
      selectedTextColor: 'text-sky-100'
    },
    'pick_up_and_delivery': {
      icon: Truck,      // Perfect - no change needed
      label: 'Pickup & Delivery',
      color: 'bg-emerald-100 text-emerald-700',
      hoverColor: 'hover:bg-emerald-200',
      selectedBg: 'bg-gradient-to-br from-emerald-500 to-emerald-600',
      selectedBorder: 'border-emerald-200',
      selectedTextColor: 'text-emerald-100'
    },
    'commercial_laundry': {
      icon: Buildings,   // Classic building representation
      label: 'Commercial Laundry',
      color: 'bg-violet-100 text-violet-700',
      hoverColor: 'hover:bg-violet-200',
      selectedBg: 'bg-gradient-to-br from-violet-500 to-violet-600',
      selectedBorder: 'border-violet-200',
      selectedTextColor: 'text-violet-100'
    },
    'dry_cleaning': {
      icon: ShirtFolded,      // Perfect for garment care
      label: 'Dry Cleaning',
      color: 'bg-rose-100 text-rose-700',
      hoverColor: 'hover:bg-rose-200',
      selectedBg: 'bg-gradient-to-br from-rose-500 to-rose-600',
      selectedBorder: 'border-rose-200',
      selectedTextColor: 'text-rose-100'
    },
    'account_wide': {
      icon: Globe,      // Global/universal symbol for account-wide
      label: 'General',
      color: 'bg-amber-100 text-amber-700',
      hoverColor: 'hover:bg-amber-200',
      selectedBg: 'bg-gradient-to-br from-amber-500 to-amber-600',
      selectedBorder: 'border-amber-200',
      selectedTextColor: 'text-amber-100'
    }
  };

  return configs[serviceType] || {
    icon: Chat,
    label: serviceType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown',
    color: 'bg-gray-100 text-gray-700',
    hoverColor: 'hover:bg-gray-200',
    selectedBg: 'bg-gradient-to-br from-gray-500 to-gray-600',
    selectedBorder: 'border-gray-200',
    selectedTextColor: 'text-gray-100'
  };
};

// Update the ServiceTypeFilter component to use dynamic service types
const ServiceTypeFilter = memo(({ 
  selectedServiceType, 
  onServiceTypeChange, 
  serviceTypeCounts, 
  isLoading,
  availableServiceTypes = [] // Add this prop
}) => {
  const totalCount = Object.values(serviceTypeCounts).reduce((sum, count) => sum + count, 0);

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100/50 p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8 backdrop-blur-sm">
      {/* Header - Mobile optimized */}
      <div className="flex items-start sm:items-center justify-between mb-6 sm:mb-8 gap-4">
        <div className="flex items-center flex-1 min-w-0">
          <div className="p-2 sm:p-3 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl mr-3 sm:mr-4 shadow-sm flex-shrink-0">
            <Funnel className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 tracking-tight truncate">Service Categories</h3>
            <p className="text-xs sm:text-sm text-gray-600 mt-0.5">Explore questions by service type</p>
          </div>
        </div>
      </div>

      {/* Show message if no service types available */}
      {availableServiceTypes.length === 0 && !isLoading && (
        <div className="text-center py-6 sm:py-8">
          <div className="text-gray-500 mb-2 text-sm sm:text-base">No service types configured for your account</div>
          <div className="text-xs sm:text-sm text-gray-400">Contact support to configure your service offerings</div>
        </div>
      )}

      {/* Filter Buttons Grid - Mobile optimized */}
      {availableServiceTypes.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 sm:gap-4">
          {/* All Services Button */}
          <button
            onClick={() => onServiceTypeChange(null)}
            disabled={isLoading}
            className={`
              group relative overflow-hidden rounded-xl sm:rounded-2xl transition-all duration-300 p-3 sm:p-4 lg:p-6 text-left
              ${selectedServiceType === null 
                ? 'bg-gradient-to-br from-indigo-500 to-blue-600 shadow-lg sm:shadow-xl scale-[1.02] sm:scale-105 border-2 border-indigo-200' 
                : 'bg-white border-2 border-gray-100 hover:border-gray-200 hover:bg-gray-50/50 hover:shadow-md hover:scale-[1.01] sm:hover:scale-102'
              }
              ${isLoading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
              transform-gpu will-change-transform
            `}
          >
            <div className="flex flex-col items-center text-center space-y-2 sm:space-y-3">
              <div className={`
                p-2.5 sm:p-3 lg:p-4 rounded-lg sm:rounded-xl transition-all duration-300 shadow-sm
                ${selectedServiceType === null 
                  ? 'bg-white/20 text-white backdrop-blur-sm' 
                  : 'bg-gradient-to-br from-gray-100 to-gray-200 text-gray-700 group-hover:from-gray-200 group-hover:to-gray-300'
                }
              `}>
                <GridFour className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
              </div>
              <div>
                <div className={`
                  font-semibold text-xs sm:text-sm transition-colors duration-300 tracking-wide leading-tight
                  ${selectedServiceType === null ? 'text-white' : 'text-gray-800'}
                `}>
                  All Categories
                </div>
                {!isLoading && (
                  <div className={`
                    text-xs font-bold mt-1 transition-colors duration-300
                    ${selectedServiceType === null ? 'text-indigo-100' : 'text-gray-500'}
                  `}>
                    {totalCount} questions
                  </div>
                )}
              </div>
            </div>
          </button>

          {/* Individual Service Type Buttons - Mobile optimized */}
          {availableServiceTypes.map(serviceType => {
            const config = getServiceTypeConfig(serviceType);
            const count = serviceTypeCounts[serviceType] || 0;
            const IconComponent = config.icon;

            // Don't show service types with 0 questions unless loading
            if (!isLoading && count === 0) return null;

            const isSelected = selectedServiceType === serviceType;

            return (
              <button
                key={serviceType}
                onClick={() => onServiceTypeChange(serviceType)}
                disabled={isLoading || count === 0}
                className={`
                  group relative overflow-hidden rounded-xl sm:rounded-2xl transition-all duration-300 p-3 sm:p-4 lg:p-6 text-left
                  ${isSelected
                    ? `${config.selectedBg} shadow-lg sm:shadow-xl scale-[1.02] sm:scale-105 border-2 ${config.selectedBorder}` 
                    : 'bg-white border-2 border-gray-100 hover:border-gray-200 hover:bg-gray-50/50 hover:shadow-md hover:scale-[1.01] sm:hover:scale-102'
                  }
                  ${isLoading || count === 0 ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
                  transform-gpu will-change-transform
                `}
              >
                <div className="flex flex-col items-center text-center space-y-2 sm:space-y-3">
                  <div className={`
                    p-2.5 sm:p-3 lg:p-4 rounded-lg sm:rounded-xl transition-all duration-300 shadow-sm
                    ${isSelected 
                      ? 'bg-white/20 text-white backdrop-blur-sm' 
                      : `${config.color} group-hover:scale-110 group-hover:shadow-md`
                    }
                  `}>
                    <IconComponent className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
                  </div>
                  <div>
                    <div className={`
                      font-semibold text-xs sm:text-sm transition-colors duration-300 leading-tight tracking-wide
                      ${isSelected ? 'text-white' : 'text-gray-800'}
                    `}>
                      {config.label}
                    </div>
                    {!isLoading && (
                      <div className={`
                        text-xs font-bold mt-1 transition-colors duration-300
                        ${isSelected ? config.selectedTextColor : 'text-gray-500'}
                      `}>
                        {count} question{count !== 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                </div>

                {/* Elegant selection indicator */}
                {isSelected && (
                  <div className="absolute top-2 right-2 sm:top-3 sm:right-3">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-white/30 rounded-full shadow-lg backdrop-blur-sm border border-white/20"></div>
                  </div>
                )}

                {/* Premium loading state */}
                {isLoading && (
                  <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center rounded-xl sm:rounded-2xl">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-gray-200 border-t-indigo-500 rounded-full animate-spin"></div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Active Filter Indicator - Mobile optimized */}
      {selectedServiceType && !isLoading && (
        <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl border border-indigo-100/50 backdrop-blur-sm">
          <div className="flex items-center">
            <div className="w-2.5 h-2.5 bg-indigo-500 rounded-full mr-2 sm:mr-3 shadow-sm flex-shrink-0"></div>
            <span className="text-sm font-semibold text-indigo-800 tracking-wide">
              Showing {getServiceTypeConfig(selectedServiceType).label} questions
            </span>
          </div>
          <button
            onClick={() => onServiceTypeChange(null)}
            className="text-xs text-indigo-600 hover:text-indigo-800 font-semibold tracking-wide underline decoration-2 underline-offset-2 transition-colors duration-200 self-start sm:self-center"
          >
            Show All
          </button>
        </div>
      )}
    </div>
  );
});

// Beautiful AI Analysis Loading Component - Steve Jobs would love this simplicity
const AIAnalysisLoading = memo(() => {
  const [dots, setDots] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  
  // Steve Jobs-level copy: Direct, honest, contextually accurate
  const steps = [
    'Checking what your AI can handle now...',
    'Comparing to common customer questions...',
    'Calculating your coverage...',
    'Almost done...'
  ];

  // Elegant dot animation
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Step progression animation
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep(prev => (prev + 1) % steps.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div 
      className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="text-center max-w-md mx-auto px-6">
        {/* Elegant AI Icon with Pulse Animation */}
        <motion.div
          className="w-20 h-20 mx-auto mb-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg"
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <Sparkle className="w-10 h-10 text-white" />
        </motion.div>

        {/* Title with subtle animation */}
        <motion.h2 
          className="text-2xl font-semibold text-gray-900 mb-3"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          Recalculating Coverage
        </motion.h2>

        {/* Current step with smooth transitions */}
        <AnimatePresence mode="wait">
          <motion.p
            key={currentStep}
            className="text-gray-600 mb-6 h-6"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            {steps[currentStep]}{dots}
          </motion.p>
        </AnimatePresence>

        {/* Progress indication */}
        <div className="w-48 mx-auto bg-gray-200 rounded-full h-1.5 mb-4 overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full"
            initial={{ width: "0%" }}
            animate={{ width: "100%" }}
            transition={{ duration: 16, ease: "easeOut" }}
          />
        </div>

        {/* Subtle message */}
        <p className="text-sm text-gray-500">
          Analyzing how your changes affect readiness
        </p>
      </div>
    </motion.div>
  );
});



// Phone number formatting helper
const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  const cleaned = phoneNumber.replace(/\D/g, '');
  const digits = cleaned.length === 11 && cleaned.startsWith('1') 
    ? cleaned.slice(1) 
    : cleaned;

  if (digits.length !== 10) {
    return phoneNumber;
  }

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const Analysis = ({ isMobile: forceMobile, onEditSetting, onCreateSetting }) => {
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);
  
  // Use the passed isMobile prop if provided, otherwise fall back to media query
  const detectedMobile = useMediaQuery("(max-width: 640px)");
  const isMobile = forceMobile !== undefined ? forceMobile : detectedMobile;

  // ESSENTIAL: Add accountId state
  const [accountId, setAccountId] = useState(null);

  // State management
  const [analysisData, setAnalysisData] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [availableSettings, setAvailableSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Template mode state
  const [isTemplateMode, setIsTemplateMode] = useState(false);
  const [hasRealData, setHasRealData] = useState(true);

  // Refresh tracking state
  const [refreshCooldown, setRefreshCooldown] = useState(null);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [canRefresh, setCanRefresh] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [cooldownEndTime, setCooldownEndTime] = useState(null);
  const [isFullRefreshing, setIsFullRefreshing] = useState(false);

  // Modal states
  const [isCallDetailsModalOpen, setIsCallDetailsModalOpen] = useState(false);
  const [selectedCallIdForModal, setSelectedCallIdForModal] = useState(null);
  const [isBrainActionModalOpen, setIsBrainActionModalOpen] = useState(false);
  const [isSimpleAnswerModalOpen, setIsSimpleAnswerModalOpen] = useState(false);
  const [isComplexAnswerModalOpen, setIsComplexAnswerModalOpen] = useState(false);
  const [isSimpleActionModalOpen, setIsSimpleActionModalOpen] = useState(false);
  const [isNewCategoryModalOpen, setIsNewCategoryModalOpen] = useState(false);

  // Current data
  const [currentSetting, setCurrentSetting] = useState(null);
  const [currentInquiry, setCurrentInquiry] = useState(null);

  // Add service type filter state
  const [selectedServiceType, setSelectedServiceType] = useState(null);

  // Add state for account's service types
  const [accountServiceTypes, setAccountServiceTypes] = useState([]);

  // Call modal states
  const [showCallModal, setShowCallModal] = useState(false);
  const [accountPhoneNumber, setAccountPhoneNumber] = useState(null);

  // Last analysis date for real data mode
  const [lastAnalysisDate, setLastAnalysisDate] = useState(null);







  // Smart cooldown calculation
  const calculateRemainingCooldown = useCallback((lastRefreshDate) => {
    if (!lastRefreshDate) return 0;
    
    const now = new Date();
    const cooldownMinutes = 60; // Updated to 1 hour
    const cooldownMs = cooldownMinutes * 60 * 1000;
    const timeSinceRefresh = now - lastRefreshDate;
    const remainingMs = cooldownMs - timeSinceRefresh;
    
    return Math.max(0, Math.ceil(remainingMs / 1000));
  }, []);

  // Initialize refresh state from database
  const initializeRefreshState = useCallback(async () => {
    if (!accountId) return;

    try {
      const { data: resultArray, error } = await supabase
        .from('calls_analysis_refresh')
        .select('last_refresh_at')
        .eq('account_id', accountId)
        .order('last_refresh_at', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Error checking refresh eligibility:', error);
        setCanRefresh(true);
        return;
      }

      const record = resultArray && resultArray.length > 0 ? resultArray[0] : null;

      if (!record) {
        setCanRefresh(true);
        setLastRefreshTime(null);
        setRefreshCooldown(null);
        setCooldownEndTime(null);
        return;
      }

      const dbRefreshTime = new Date(record.last_refresh_at);
      const dbRemainingCooldown = calculateRemainingCooldown(dbRefreshTime);
      
      setLastRefreshTime(dbRefreshTime);

      if (dbRemainingCooldown <= 0) {
        setCanRefresh(true);
        setRefreshCooldown(null);
        setCooldownEndTime(null);
      } else {
        setCanRefresh(false);
        setRefreshCooldown(dbRemainingCooldown);
        
        const endTime = new Date(dbRefreshTime.getTime() + (30 * 60 * 1000));
        setCooldownEndTime(endTime);
      }

    } catch (error) {
      console.error('Error initializing refresh state:', error);
      setCanRefresh(true);
    }
  }, [accountId, supabase, calculateRemainingCooldown]);

  // Countdown timer
  useEffect(() => {
    if (!cooldownEndTime) return;

    const updateCountdown = () => {
      const now = new Date();
      const remainingMs = cooldownEndTime - now;
      
      if (remainingMs <= 0) {
        setRefreshCooldown(null);
        setCooldownEndTime(null);
        setCanRefresh(true);
      } else {
        setRefreshCooldown(Math.ceil(remainingMs / 1000));
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(interval);
  }, [cooldownEndTime]);

  // Update refresh tracking
  const updateRefreshTracking = async () => {
    if (!accountId) return;

    try {
      const { data: existingRecords, error: selectError } = await supabase
        .from('calls_analysis_refresh')
        .select('id')
        .eq('account_id', accountId)
        .limit(1);

      if (selectError) {
        console.error('Error checking for existing refresh record:', selectError);
        return;
      }

      const existingRecord = existingRecords && existingRecords.length > 0 ? existingRecords[0] : null;
      const timestamp = new Date().toISOString();
      const refreshTime = new Date(timestamp);

      if (existingRecord) {
        const { error: updateError } = await supabase
          .from('calls_analysis_refresh')
          .update({
            last_refresh_at: timestamp,
            updated_at: timestamp
          })
          .eq('account_id', accountId);

        if (updateError) {
          console.error('Error updating refresh tracking:', updateError);
          return;
        }
      } else {
        const { error: insertError } = await supabase
          .from('calls_analysis_refresh')
          .insert({
            account_id: accountId,
            last_refresh_at: timestamp,
            created_at: timestamp,
            updated_at: timestamp
          });

        if (insertError) {
          console.error('Error inserting refresh tracking:', insertError);
          return;
        }
      }

      setLastRefreshTime(refreshTime);
      setCanRefresh(false);
      
      const cooldownEndTime = new Date(refreshTime.getTime() + (60 * 60 * 1000)); // Updated to 1 hour
      setCooldownEndTime(cooldownEndTime);
      setRefreshCooldown(60 * 60); // Updated to 1 hour

      console.log('Refresh tracking updated successfully');

    } catch (error) {
      console.error('Error with refresh tracking operation:', error);
    }
  };

  // Manual refresh function
  const handleManualRefresh = async () => {
    if (!canRefresh || isAnalyzing) return;

    await updateRefreshTracking();
    
    // Perform the actual analysis refresh - these functions handle their own loading states
    if (isTemplateMode) {
      await fetchTemplateAnalysisData(true);
    } else {
      await fetchAnalysisData();
    }
  };

  // Full coverage refresh function
  const handleFullCoverageRefresh = async () => {
    if (!canRefresh || isFullRefreshing || isAnalyzing) return;

    try {
      setIsFullRefreshing(true);
      
      // Call the new endpoint
      const response = await fetch(`${API_BASE_URL}${endpoints.coverageRefresh}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          account_id: accountId
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to start full coverage refresh');
      }

      // Show a persistent toast and manually start the UI cooldown
      toast.info('Full coverage refresh is in progress. We will notify you when it is complete.', {
        id: 'refresh-toast' // Give it an ID to update it later
      });

      // Immediately start the cooldown on the UI to prevent re-clicks.
      // This provides a better UX than waiting for the subscription to update.
      const now = new Date();
      const endTime = new Date(now.getTime() + (60 * 60 * 1000));
      setCooldownEndTime(endTime);
      setCanRefresh(false);
      
    } catch (error) {
      console.error('Error starting full coverage refresh:', error);
      toast.error(error.message || 'Failed to start full coverage refresh. Please try again.');
    } finally {
      setIsFullRefreshing(false);
    }
  };

  // Format countdown for display
  const formatCountdown = (seconds) => {
    if (!seconds) return '';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Get accountId on component mount
  useEffect(() => {
    const getAccountId = async () => {
      try {
        const { getUserAccountId } = await import('../utils/accountUtils');
        const { accountId, error: accountIdError } = await getUserAccountId();
        if (accountIdError) throw new Error(accountIdError);
        setAccountId(accountId);
      } catch (error) {
        console.error('Error getting account ID:', error);
        setError('Failed to authenticate user');
      }
    };

    getAccountId();
  }, [supabase]);

  // Simple data mode detection - template mode until 100+ analysis records
  const detectDataMode = useCallback(async () => {
    if (!accountId) return;
    
    try {
      // Count total rows in calls_analysis for this account
      const { count, error } = await supabase
        .from('calls_analysis')
        .select('*', { count: 'exact', head: true })
        .eq('account_id', accountId);

      if (error) throw error;

      // Switch to real data mode only when account has 100+ analysis records
      const hasEnoughData = count >= 100;
      setHasRealData(hasEnoughData);
      setIsTemplateMode(!hasEnoughData);
      
      
    } catch (err) {
      console.error('Error detecting data mode:', err);
      setIsTemplateMode(true);
      setHasRealData(false);
    }
  }, [supabase, accountId]);

// Enhanced backend coverage analysis with Curbside integration awareness
const getCoverageFromBackend = useCallback(async (inquiries) => {
  if (!accountId || !inquiries.length) return inquiries;

  try {
    // STEP 1: Check for active Curbside integration
    const { data: curbsideData, error: curbsideError } = await supabase
      .from('curbside_settings')
      .select('integration_status, url')
      .eq('account_id', accountId)
      .eq('integration_status', 'complete')
      .maybeSingle();

    const hasCurbsideIntegration = !curbsideError && curbsideData;

    // STEP 2: Define Curbside-related inquiry patterns
    const curbsideCapabilities = [
      'Create customer accounts',
      'Place orders', 
      'Edit existing orders',
      'Check order status'
    ];

    const curbsideKeywords = [
      // Order-related
      'order', 'place order', 'new order', 'create order', 'submit order',
      'order status', 'check order', 'order progress', 'where is my order',
      'modify order', 'change order', 'edit order', 'update order', 'cancel order',
      
      // Account-related  
      'account', 'create account', 'new account', 'sign up', 'register',
      'customer account', 'my account', 'login', 'profile',
      
      // Pickup/delivery related
      'pickup', 'delivery', 'when will', 'ready for pickup', 'schedule pickup',
      'pickup time', 'delivery time', 'pickup window', 'delivery window',
      
      // Service status
      'ready', 'complete', 'finished', 'done', 'progress', 'status'
    ];

    // STEP 3: Pre-process inquiries for Curbside coverage
    let preprocessedInquiries = inquiries.map(item => {
      if (hasCurbsideIntegration) {
        const inquiryLower = item.inquiry.toLowerCase();
        const matchesCurbside = curbsideKeywords.some(keyword => 
          inquiryLower.includes(keyword.toLowerCase())
        );

        if (matchesCurbside) {
          // Mark as covered by Curbside integration
          return {
            ...item,
            covered: true,
            brain_setting_id: 'curbside-integration',
            brain_setting_name: 'Curbside POS',
            brain_setting_type: 'integration',
            brain_category_name: 'Connected Integrations',
            action_type: null,
            full_brain_setting: {
              id: 'curbside-integration',
              name: 'Curbside POS',
              type: 'integration',
              brain_category: { name: 'Connected Integrations' },
              capabilities: curbsideCapabilities
            }
          };
        }
      }
      return item;
    });

    // STEP 4: Filter out already covered items for backend analysis
    const uncoveredInquiries = preprocessedInquiries.filter(item => !item.covered);

    // STEP 5: Send account ID to backend for AI analysis (backend handles its own data fetching)
    if (uncoveredInquiries.length > 0) {
      const response = await fetch(`${API_BASE_URL}${endpoints.analysisCoverage}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          account_id: accountId,
          inquiries: uncoveredInquiries
        })
      });

      if (response.ok) {
        const coverageData = await response.json();
        
        if (coverageData.success && Array.isArray(coverageData.data)) {
          const coverageMap = new Map();
          coverageData.data.forEach(item => {
            coverageMap.set(item.id, {
              covered: item.covered,
              settings_id: item.settings_id,
              brain_setting: item.brain_setting
            });
          });
          
          // STEP 6: Merge backend results with Curbside results
          preprocessedInquiries = preprocessedInquiries.map(item => {
            if (item.covered) {
              // Already covered by Curbside - keep as is
              return item;
            }
            
            // Update with backend analysis results
            const backendResult = coverageMap.get(item.id);
            const brainSetting = backendResult?.brain_setting;
            
            return {
              ...item,
              covered: backendResult?.covered || false,
              brain_setting_id: backendResult?.settings_id || null,
              brain_setting_name: brainSetting?.name || null,
              brain_setting_type: brainSetting?.type || null,
              brain_category_name: brainSetting?.brain_category?.name || null,
              action_type: brainSetting?.type === 'simple_action' ? brainSetting.primary_content?.action_type : null,
              full_brain_setting: brainSetting
            };
          });
        } else {
          console.warn('Backend returned unsuccessful response:', coverageData.error || 'Invalid data format');
        }
      } else {
        console.warn(`AI analysis failed (${response.status}), using Curbside coverage only`);
      }
    }

    return preprocessedInquiries;

  } catch (error) {
    console.warn('Coverage analysis error:', error);
    return inquiries;
  }
}, [accountId, supabase]);

  // Simple template data fetching with database storage
  // Helper function to check if coverage data exists
  const checkExistingCoverage = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('calls_analysis_onboarding')
        .select('template_inquiry_id')
        .eq('account_id', accountId)
        .limit(1);
      
      if (error) return false;
      return data && data.length > 0;
    } catch {
      return false;
    }
  }, [supabase, accountId]);

  const fetchTemplateAnalysisData = useCallback(async (forceRefresh = false, preserveAnalyzingState = false) => {
    if (!accountId) return;

    // Make sure we have account service types loaded first
    if (accountServiceTypes.length === 0) {
      return;
    }

    try {
      // Only run AI analysis if explicitly forced (from modal saves)
      const hasCoverageData = await checkExistingCoverage();
      const shouldRunAnalysis = forceRefresh; // SIMPLIFIED: Only analyze when explicitly requested
      
      if (shouldRunAnalysis) {
        setIsAnalyzing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // STEP 1: Fetch template inquiries - ONLY for service types this account offers
      const { data: templateData, error: templateError } = await supabase
        .from('calls_analysis_template_inquiries')
        .select(`
          id,
          inquiry,
          type,
          priority,
          created_at,
          category_id,
          service_type,
          calls_analysis_categories!inner(
            id,
            category_name,
            friendly_name
          )
        `)
        .eq('business_type', businessType)
        .in('service_type', accountServiceTypes)
        .order('priority', { ascending: true });

      if (templateError) throw templateError;

      // STEP 2: Check calls_analysis_onboarding for existing coverage data
      const { data: existingCoverage, error: coverageError } = await supabase
        .from('calls_analysis_onboarding')
        .select(`
          template_inquiry_id,
          covered,
          brain_setting_id,
          brain_settings(
            id,
            name,
            type,
            category_id,
            brain_categories(name),
            brain_simple_actions(action_type),
            brain_simple_answers(title, content),
            brain_complex_answers(*)
          )
        `)
        .eq('account_id', accountId);

      if (coverageError) throw coverageError;

      // STEP 3: Create coverage map
      const coverageMap = new Map();
      if (existingCoverage) {
        existingCoverage.forEach(item => {
          coverageMap.set(item.template_inquiry_id, {
            covered: item.covered,
            brain_setting_id: item.brain_setting_id,
            brain_setting: item.brain_settings
          });
        });
      }

      // STEP 4: Transform data with existing coverage (default to uncovered for new users)
      let transformedTemplate = templateData.map(item => {
        const coverage = coverageMap.get(item.id);
        const brainSetting = coverage?.brain_setting;
        
        return {
          id: item.id,
          call_id: null,
          type: item.type,
          inquiry: item.inquiry,
          created_at: item.created_at,
          category_id: item.category_id,
          service_type: item.service_type,
          priority: item.priority,
          covered: coverage?.covered || false, // Shows uncovered until user saves something
          brain_setting_id: coverage?.brain_setting_id || null,
          brain_setting_name: brainSetting?.name || null,
          brain_setting_type: brainSetting?.type || null,
          brain_category_name: brainSetting?.brain_categories?.name || null,
          action_type: brainSetting?.type === 'simple_action' ? brainSetting.brain_simple_actions?.[0]?.action_type : null,
          call_created_at: null,
          call_category: null,
          isTemplate: true,
          full_brain_setting: brainSetting
        };
      });

      // STEP 5: Run AI analysis if no coverage data exists or forced refresh
      if (shouldRunAnalysis) {
        const itemsToAnalyze = forceRefresh ? transformedTemplate : transformedTemplate.filter(item => !item.covered);
        
        if (itemsToAnalyze.length > 0) {
          const analyzedItems = await getCoverageFromBackend(itemsToAnalyze);
          
          // Save coverage results to database
          const coverageUpserts = analyzedItems.map(item => ({
            account_id: accountId,
            template_inquiry_id: item.id,
            covered: item.covered,
            brain_setting_id: item.brain_setting_id
          }));

          const { error: upsertError } = await supabase
            .from('calls_analysis_onboarding')
            .upsert(coverageUpserts, { 
              onConflict: 'account_id,template_inquiry_id',
              ignoreDuplicates: false 
            });

          if (upsertError) {
            console.error('Failed to save coverage data:', upsertError);
          }

          // Update with analyzed results
          const analyzedMap = new Map(analyzedItems.map(item => [item.id, item]));
          transformedTemplate = transformedTemplate.map(item => 
            analyzedMap.get(item.id) || item
          );
        }
      }

      // STEP 6: Organize by categories
      const { data: categories, error: categoriesError } = await supabase
        .from('calls_analysis_categories')
        .select('*')
        .order('category_name');

      if (categoriesError) throw categoriesError;

      const groupedByCategory = transformedTemplate.reduce((acc, inquiry) => {
        const categoryId = inquiry.category_id;
        if (categoryId) {
          if (!acc[categoryId]) acc[categoryId] = [];
          acc[categoryId].push(inquiry);
        }
        return acc;
      }, {});

      const categoriesWithInquiries = categories.map(category => ({
        ...category,
        inquiries: (groupedByCategory[category.id] || []).sort((a, b) => {
          const priorityOrder = { essential: 1, important: 2, helpful: 3 };
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        })
      }));

      const filteredCategories = categoriesWithInquiries
        .filter(cat => cat.inquiries.length > 0)
        .sort((a, b) => (b.inquiries?.length || 0) - (a.inquiries?.length || 0));

      setCategoriesData(filteredCategories);
      setAnalysisData(transformedTemplate);

      const coveredCount = transformedTemplate.filter(item => item.covered).length;
      

    } catch (err) {
      console.error("Error fetching template analysis data:", err);
      setError(err.message || "Failed to fetch template analysis data.");
    } finally {
      setLoading(false);
      // Remove setIsAnalyzing(false) - now managed by handleModalClose for consistent UX
    }
  }, [supabase, accountId, getCoverageFromBackend, accountServiceTypes, businessType, checkExistingCoverage]);

  // Simple fetch analysis data for real data mode
  const fetchAnalysisData = useCallback(async () => {
    if (!accountId) return;

    try {
      setLoading(true);
      setError(null);

      const [categoriesResult, analysisResult] = await Promise.all([
        supabase
          .from('calls_analysis_categories')
        .select('*')
          .order('category_name'),

        supabase
          .from('calls_analysis')
          .select(`
            id,
            call_id,
            type,
            inquiry,
            created_at,
            category_id,
            calls_analysis_coverage!left (
              covered,
              brain_settings (
                id,
                name,
                type,
                category_id,
                brain_categories (name),
                brain_simple_actions (action_type)
              )
            ),
            calls!inner (created_at, call_category)
          `)
          .eq('account_id', accountId)
          .order('created_at', { ascending: false })
      ]);

      if (categoriesResult.error) throw categoriesResult.error;
      if (analysisResult.error) throw analysisResult.error;

      const transformedAnalysis = analysisResult.data.map(item => ({
        id: item.id,
        call_id: item.call_id,
        type: item.type,
        inquiry: item.inquiry,
        created_at: item.created_at,
        category_id: item.category_id,
        covered: item.calls_analysis_coverage?.covered,
        brain_setting_id: item.calls_analysis_coverage?.brain_settings?.id,
        brain_setting_name: item.calls_analysis_coverage?.brain_settings?.name,
        brain_setting_type: item.calls_analysis_coverage?.brain_settings?.type,
        brain_category_name: item.calls_analysis_coverage?.brain_settings?.brain_categories?.name,
        action_type: item.calls_analysis_coverage?.brain_settings?.brain_simple_actions?.[0]?.action_type,
        call_created_at: item.calls?.created_at,
        call_category: item.calls?.call_category
      }));

      const groupedByCategory = transformedAnalysis.reduce((acc, inquiry) => {
        const categoryId = inquiry.category_id;
        if (categoryId) {
          if (!acc[categoryId]) acc[categoryId] = [];
          acc[categoryId].push(inquiry);
        }
          return acc;
        }, {});

      const categoriesWithInquiries = categoriesResult.data.map(category => ({
        ...category,
        inquiries: (groupedByCategory[category.id] || []).sort((a, b) => {
          const dateA = new Date(a.call_created_at || a.created_at);
          const dateB = new Date(b.call_created_at || b.created_at);
          return dateB - dateA;
        })
      }));

      const filteredCategories = categoriesWithInquiries.sort((a, b) => (b.inquiries?.length || 0) - (a.inquiries?.length || 0));

      setCategoriesData(filteredCategories);
      setAnalysisData(transformedAnalysis);

    } catch (err) {
      console.error("Error fetching analysis data:", err);
      setError(err.message || "Failed to fetch analysis data.");
    } finally {
      setLoading(false);
    }
  }, [supabase, accountId]);

  // Fetch available settings
  const fetchAvailableSettings = useCallback(async () => {
    if (!accountId) return;

    try {
      const { data, error } = await supabase
        .from('brain_settings')
        .select(`
          id,
          name,
          type,
          category_id,
          brain_categories!inner(
            account_id,
            name
          ),
          brain_simple_actions(action_type)
        `)
        .eq('brain_categories.account_id', accountId)
        .order('name');

      if (error) throw error;

      const settingsWithActionType = data.map(setting => ({
        ...setting,
        action_type: setting.brain_simple_actions?.[0]?.action_type,
        brain_category_name: setting.brain_categories?.name
      }));

      setAvailableSettings(settingsWithActionType);
    } catch (err) {
      console.error('Error fetching available settings:', err);
    }
  }, [supabase, accountId]);

  // Data loading effects
  useEffect(() => {
    if (accountId) {
      detectDataMode();
    }
  }, [accountId, detectDataMode]);

  useEffect(() => {
    if (accountId && isTemplateMode !== undefined) {
      if (isTemplateMode) {
        // Template data fetching is handled by separate useEffect that waits for service types
      } else {
        fetchAnalysisData();
      }
    }
  }, [accountId, isTemplateMode, fetchAnalysisData]);

  useEffect(() => {
    if (accountId && !loading && categoriesData.length > 0) {
      fetchAvailableSettings();
    }
  }, [accountId, loading, categoriesData.length, fetchAvailableSettings]);

  // Fetch account service types
  const fetchAccountServiceTypes = useCallback(async () => {
    if (!accountId) return;

    try {
      const { data, error } = await supabase
        .from('accounts_services_types')
        .select(`
          system_service_types!inner(
            name,
            display_order
          )
        `)
        .eq('account_id', accountId);

      if (error) throw error;

      const sortedServiceTypes = data
        .sort((a, b) => a.system_service_types.display_order - b.system_service_types.display_order)
        .map(item => item.system_service_types.name);

      setAccountServiceTypes(sortedServiceTypes);
    } catch (err) {
      console.error('Error fetching account service types:', err);
      setAccountServiceTypes([]);
    }
  }, [supabase, accountId]);

  // Fetch account phone number for call modal
  const fetchAccountPhoneNumber = useCallback(async () => {
    if (!accountId) return;

    try {
      const { data: settings, error } = await supabase
        .from('vapi_call_settings')
        .select('phone_number')
        .eq('account_id', accountId)
        .maybeSingle();

      if (error) throw error;

      setAccountPhoneNumber(settings?.phone_number || null);
    } catch (error) {
      console.error('Error fetching account phone number:', error);
      setAccountPhoneNumber(null);
    }
  }, [supabase, accountId]);

  // Fetch last analysis date for real data mode
  const fetchLastAnalysisDate = useCallback(async () => {
    if (!accountId) return;

    try {
      const { data, error } = await supabase
        .from('calls_analysis')
        .select('created_at')
        .eq('account_id', accountId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        // If no data found, that's okay - just set to null
        setLastAnalysisDate(null);
        return;
      }

      setLastAnalysisDate(data?.created_at ? new Date(data.created_at) : null);
    } catch (error) {
      console.error('Error fetching last analysis date:', error);
      setLastAnalysisDate(null);
    }
  }, [supabase, accountId]);

  // Event handlers
  const handleServiceTypeChange = useCallback((serviceType) => {
    setSelectedServiceType(serviceType);
    setExpandedItems([]);
  }, []);

  const handleViewCallDetails = useCallback((callId) => {
    setSelectedCallIdForModal(callId);
    setIsCallDetailsModalOpen(true);
  }, []);

  // Updated handleEditSetting function
const handleEditSetting = useCallback(async (inquiry) => {
  // If we have a handler passed from Brain.js, use it
  if (onEditSetting) {
    onEditSetting(inquiry);
    return;
  }

  // Fallback to original modal behavior for standalone usage
  if (!inquiry.brain_setting_id || !inquiry.brain_setting_type) {
    console.warn('Missing required fields for editing setting:', inquiry);
    return;
  }

  let settingData = null;

  try {
    if (inquiry.full_brain_setting) {
      // Use cached data from coverage analysis
      settingData = inquiry.full_brain_setting;
      
      if (inquiry.brain_setting_type === 'simple_answer') {
        let questionText = '';
        let answerText = '';
        
        if (settingData.brain_simple_answers && settingData.brain_simple_answers.length > 0) {
          const simpleAnswer = settingData.brain_simple_answers[0];
          questionText = simpleAnswer.title || settingData.name || '';
          answerText = simpleAnswer.content || '';
        } else {
          questionText = settingData.name || '';
          answerText = settingData.content || '';
        }
        
        settingData = {
          ...settingData,
          settings_id: settingData.id || inquiry.brain_setting_id,
          question: questionText,
          answer: answerText,
          category_id: settingData.category_id
        };
        
      } else if (inquiry.brain_setting_type === 'complex_answer') {
        settingData = {
          ...settingData,
          id: settingData.id || inquiry.brain_setting_id
        };
        
      } else if (inquiry.brain_setting_type === 'simple_action') {
        let actionType = 'api';
        
        if (settingData.brain_simple_actions && settingData.brain_simple_actions.length > 0) {
          actionType = settingData.brain_simple_actions[0].action_type || 'api';
        }
        
        settingData = {
          ...settingData,
          settings_id: settingData.id || inquiry.brain_setting_id,
          action_type: actionType
        };
      }
    } else {
      // Load fresh data from database
      const { data, error } = await supabase
        .from('brain_settings')
        .select(`
          *,
          brain_categories (name),
          brain_simple_answers (title, content),
          brain_complex_answers (*),
          brain_simple_actions (action_type, *),
          brain_complex_actions (*)
        `)
        .eq('id', inquiry.brain_setting_id)
        .maybeSingle();
      
      if (error) {
        console.error('Error loading setting from database:', error);
        toast.error('Failed to load setting details. Please try again.');
        return;
      }

      if (inquiry.brain_setting_type === 'simple_answer') {
        settingData = {
          ...data,
          settings_id: data.id,
          question: data.brain_simple_answers?.[0]?.title || data.name || '',
          answer: data.brain_simple_answers?.[0]?.content || '',
          category_id: data.category_id
        };
      } else if (inquiry.brain_setting_type === 'complex_answer') {
        settingData = {
          ...data,
          id: data.id
        };
      } else if (inquiry.brain_setting_type === 'simple_action') {
        settingData = {
          ...data,
          settings_id: data.id,
          action_type: data.brain_simple_actions?.[0]?.action_type || 'api'
        };
      }
    }

    if (!settingData) {
      console.error('No setting data available');
      toast.error('Unable to load setting details. Please try again.');
      return;
    }

    setCurrentSetting(settingData);
    
    switch (inquiry.brain_setting_type) {
      case 'simple_answer':
        setIsSimpleAnswerModalOpen(true);
        break;
      case 'complex_answer':
        setIsComplexAnswerModalOpen(true);
        break;
      case 'simple_action':
        setIsSimpleActionModalOpen(true);
        break;
      default:
        console.warn('Unknown brain setting type:', inquiry.brain_setting_type);
        toast.error('Unsupported setting type. Please contact support.');
    }

  } catch (error) {
    console.error('Error loading setting for modal:', error);
    toast.error('Failed to load setting details. Please try again.');
  }
}, [supabase, onEditSetting]);

  const handleCreateSetting = useCallback((inquiry) => {
    // If we have a handler passed from Brain.js, use it
    if (onCreateSetting) {
      onCreateSetting(inquiry);
      return;
    }

    // Fallback to original modal behavior for standalone usage
    setCurrentInquiry(inquiry);
    setIsBrainActionModalOpen(true);
  }, [onCreateSetting]);

  const handleUpdateExisting = useCallback(async (inquiry, existingSetting) => {
    // If we have a handler passed from Brain.js, use it for editing
    if (onEditSetting) {
      // Create a mock inquiry object with the existing setting data
      const mockInquiry = {
        brain_setting_id: existingSetting.id,
        brain_setting_type: existingSetting.type,
        inquiry: inquiry.inquiry
      };
      onEditSetting(mockInquiry);
      return;
    }

    // Fallback to original modal behavior for standalone usage
    if (!existingSetting.id || !existingSetting.type) {
      console.warn('Missing required fields for updating existing setting:', existingSetting);
      return;
    }

    let settingData = null;

    try {
      // Fetch complete setting data from database
      const { data, error } = await supabase
        .from('brain_settings')
        .select(`
          *,
          brain_categories (name),
          brain_simple_answers (title, content),
          brain_complex_answers (*),
          brain_simple_actions (action_type, *),
          brain_complex_actions (*)
        `)
        .eq('id', existingSetting.id)
        .maybeSingle();
      
      if (error) {
        console.error('Error loading setting from database:', error);
        toast.error('Failed to load setting details. Please try again.');
        return;
      }

      // Transform data based on setting type
      if (existingSetting.type === 'simple_answer') {
        settingData = {
          ...data,
          settings_id: data.id,
          question: data.brain_simple_answers?.[0]?.title || data.name || '',
          answer: data.brain_simple_answers?.[0]?.content || '',
          category_id: data.category_id
        };
      } else if (existingSetting.type === 'complex_answer') {
        settingData = {
          ...data,
          id: data.id
        };
      } else if (existingSetting.type === 'simple_action') {
        settingData = {
          ...data,
          settings_id: data.id,
          action_type: data.brain_simple_actions?.[0]?.action_type || 'api'
        };
      }

      if (!settingData) {
        console.error('No setting data available');
        toast.error('Unable to load setting details. Please try again.');
        return;
      }

      setCurrentSetting(settingData);
      setCurrentInquiry(inquiry);

      // Open appropriate modal
      if (existingSetting.type === 'simple_answer') {
        setIsSimpleAnswerModalOpen(true);
      } else if (existingSetting.type === 'complex_answer') {
        setIsComplexAnswerModalOpen(true);
      } else if (existingSetting.type === 'simple_action') {
        setIsSimpleActionModalOpen(true);
      }

    } catch (error) {
      console.error('Error loading setting for modal:', error);
      toast.error('Failed to load setting details. Please try again.');
    }
  }, [supabase, onEditSetting]);

  const handleBrainActionSelect = useCallback((actionType) => {
    setIsBrainActionModalOpen(false);
    const baseData = {
      category_id: currentInquiry?.category_id || '',
      name: currentInquiry?.inquiry || '',
      description: `Setting created from inquiry: ${currentInquiry?.inquiry}`
    };

    switch (actionType) {
      case 'simple_answer':
        setCurrentSetting({ ...baseData, question: currentInquiry?.inquiry || '', answer: '' });
        setIsSimpleAnswerModalOpen(true);
        break;
      case 'complex_answer':
        setCurrentSetting(baseData);
        setIsComplexAnswerModalOpen(true);
        break;
      case 'simple_action':
        setCurrentSetting(baseData);
        setIsSimpleActionModalOpen(true);
        break;
      case 'create_category':
        setIsNewCategoryModalOpen(true);
        break;
      default:
        console.error("Unknown action type");
    }
  }, [currentInquiry]);

  const handleModalClose = useCallback(async (reason = 'cancel') => {
    // Store the current inquiry before clearing state
    const inquiryBeingSaved = currentInquiry;
    
    // Close modals immediately first to match Brain.js behavior
    setCurrentSetting(null);
    setCurrentInquiry(null);
    setIsSimpleAnswerModalOpen(false);
    setIsComplexAnswerModalOpen(false);
    setIsSimpleActionModalOpen(false);
    setIsNewCategoryModalOpen(false);
    setIsBrainActionModalOpen(false);

    // Only run expensive AI analysis if data was actually saved
    if (reason === 'save') {
      // Start the beautiful loading animation for actual saves
      setIsAnalyzing(true);

      try {
        if (isTemplateMode && inquiryBeingSaved) {
          // GRANULAR ANALYSIS: Only analyze the specific inquiry that was saved
          console.log('[Analysis.js] Analyzing specific inquiry after save:', inquiryBeingSaved.inquiry);
          
          // Clear coverage cache for this specific inquiry only
          const { error: clearError } = await supabase
            .from('calls_analysis_onboarding')
            .delete()
            .eq('account_id', accountId)
            .eq('template_inquiry_id', inquiryBeingSaved.id);
          
          if (clearError) {
            console.error('Failed to clear coverage cache for specific inquiry:', clearError);
          }

          // Use the getCoverageFromBackend function to analyze just this inquiry
          const analyzedInquiries = await getCoverageFromBackend([inquiryBeingSaved]);
          
          if (analyzedInquiries && analyzedInquiries.length > 0) {
            const analyzedInquiry = analyzedInquiries[0];
            
            // Save the specific coverage result
            const { error: upsertError } = await supabase
              .from('calls_analysis_onboarding')
              .upsert({
                account_id: accountId,
                template_inquiry_id: analyzedInquiry.id,
                covered: analyzedInquiry.covered,
                brain_setting_id: analyzedInquiry.brain_setting_id
              }, { 
                onConflict: 'account_id,template_inquiry_id',
                ignoreDuplicates: false 
              });

            if (upsertError) {
              console.error('Failed to save specific coverage data:', upsertError);
            }
          }
          
          // Refresh the display data to show the updated coverage
          await fetchTemplateAnalysisData();
          
        } else if (isTemplateMode) {
          // BULK ANALYSIS: Fall back to analyzing all inquiries if no specific inquiry
          console.log('[Analysis.js] No specific inquiry, running bulk analysis');
          
          // Clear all coverage cache
          const { error } = await supabase
            .from('calls_analysis_onboarding')
            .delete()
            .eq('account_id', accountId);
          
          if (error) {
            console.error('Failed to clear coverage cache:', error);
          }
          
          await fetchTemplateAnalysisData(true);
        } else {
          // REAL DATA MODE: Use existing logic
          await fetchAnalysisData();
        }
      } finally {
        // Always reset analyzing state after everything completes
        setIsAnalyzing(false);
      }
    }
    // When canceling: do nothing - client-side cache is fresh since no DB changes occurred
  }, [isTemplateMode, accountId, supabase, fetchTemplateAnalysisData, fetchAnalysisData, currentInquiry, getCoverageFromBackend]);

  const toggleExpand = useCallback((categoryId) => {
    setExpandedItems(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  }, []);

  // Stats calculation
  const { stats, filteredAnalysisData, filteredCategoriesData, serviceTypeCounts } = useMemo(() => {
    const counts = {};
    analysisData.forEach(item => {
      if (item.service_type) {
        counts[item.service_type] = (counts[item.service_type] || 0) + 1;
      }
    });

    const filteredData = selectedServiceType 
      ? analysisData.filter(item => item.service_type === selectedServiceType)
      : analysisData;

    const filteredCategories = categoriesData.map(category => ({
      ...category,
      inquiries: category.inquiries?.filter(inquiry => 
        selectedServiceType 
          ? inquiry.service_type === selectedServiceType
          : true
      ) || []
    })).filter(category => category.inquiries.length > 0);

    const total = filteredData.length;
    const covered = filteredData.filter(item => item.covered === true).length;
    const coveragePercentage = total > 0 ? Math.round((covered / total) * 100) : 0;

    return {
      stats: { total, covered, coveragePercentage },
      filteredAnalysisData: filteredData,
      filteredCategoriesData: filteredCategories,
      serviceTypeCounts: counts
    };
  }, [analysisData, categoriesData, selectedServiceType]);

  // Fetch account service types when accountId is available
  useEffect(() => {
    if (accountId) {
      fetchAccountServiceTypes();
      fetchAccountPhoneNumber();
      
      // Fetch last analysis date for real data mode
      if (!isTemplateMode) {
        fetchLastAnalysisDate();
      }
      
      // Check if we should open call modal (from notifications page)
      const shouldOpenCallModal = localStorage.getItem('openCallModal') === 'true';
      if (shouldOpenCallModal) {
        localStorage.removeItem('openCallModal');
        setShowCallModal(true);
      }
    }
  }, [accountId, fetchAccountServiceTypes, fetchAccountPhoneNumber, fetchLastAnalysisDate, isTemplateMode]);

  // Trigger template data fetch when account service types are loaded
  useEffect(() => {
    if (accountId && isTemplateMode && accountServiceTypes.length > 0) {
      fetchTemplateAnalysisData();
    }
  }, [accountId, isTemplateMode, accountServiceTypes, fetchTemplateAnalysisData]);

  // Check refresh eligibility on mount
  useEffect(() => {
    if (accountId) {
      initializeRefreshState();
    }
  }, [accountId, initializeRefreshState]);

  // Periodic sync to handle edge cases
  useEffect(() => {
    if (!accountId || !cooldownEndTime) return;

    const syncInterval = setInterval(() => {
      initializeRefreshState();
    }, 5 * 60 * 1000);

    return () => clearInterval(syncInterval);
  }, [accountId, cooldownEndTime, initializeRefreshState]);

  // Real-time subscription to calls_analysis_onboarding - auto-refresh when coverage changes
  useEffect(() => {
    if (!supabase || !accountId) return;

    let subscription = null;

    const setupLiveUpdates = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Monitor calls_analysis_onboarding table for changes (onboarding coverage updates)
      subscription = supabase
        .channel('onboarding-coverage-updates')
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'calls_analysis_onboarding',
          filter: `account_id=eq.${accountId}`
        }, (payload) => {
          // Debounced refresh for smooth UX - only refresh template data
          if (isTemplateMode) {
            setTimeout(() => {
              fetchTemplateAnalysisData();
            }, 1000);
          }
        })
        .subscribe();
    };

    setupLiveUpdates();

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [supabase, accountId, isTemplateMode, fetchTemplateAnalysisData]);

  // Real-time subscription for full coverage refresh completion
  useEffect(() => {
    if (!supabase || !accountId) return;

    const refreshChannel = supabase
      .channel(`analysis-refresh-completion-${accountId}`)
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'calls_analysis_refresh',
        filter: `account_id=eq.${accountId}`
      }, (payload) => {
        // A change was detected, indicating the backend job is done.
        console.log('Full coverage refresh complete, detected by subscription:', payload);
        
        // Update the toast to show completion
        toast.success('Full coverage refresh is complete!', {
          id: 'refresh-toast'
        });

        // Refresh the analysis data to show the new results
        if (isTemplateMode) {
          fetchTemplateAnalysisData();
        } else {
          fetchAnalysisData();
        }
        
        // Re-initialize the refresh state to get the new cooldown from the database.
        initializeRefreshState();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(refreshChannel);
    };
  }, [supabase, accountId, isTemplateMode, fetchTemplateAnalysisData, fetchAnalysisData, initializeRefreshState]);

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <WarningCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Loading Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => {
              if (isTemplateMode) {
                fetchTemplateAnalysisData();
              } else {
                fetchAnalysisData();
              }
            }}
            className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Beautiful AI Analysis Loading - Show during analysis
  /* if (isAnalyzing) {
    return <AIAnalysisLoading />;
  } */

  return (
    <motion.div 
      className={`h-full bg-gradient-to-br from-gray-50 via-white to-blue-50 ${isMobile ? 'force-mobile' : ''}`}
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Force mobile styles when in narrow container */}
      {isMobile && (
        <style>{`
          .force-mobile .sm\\:p-5 { padding: 1rem !important; }
          .force-mobile .sm\\:text-sm { font-size: 0.875rem !important; }
          .force-mobile .sm\\:text-2xl { font-size: 1.5rem !important; }
          .force-mobile .sm\\:space-x-3 > * + * { margin-left: 0.5rem !important; }
          .force-mobile .sm\\:px-4 { padding-left: 0.75rem !important; padding-right: 0.75rem !important; }
          .force-mobile .sm\\:py-6 { padding-top: 1rem !important; padding-bottom: 1rem !important; }
          .force-mobile .sm\\:gap-4 { gap: 0.75rem !important; }
          .force-mobile .sm\\:mb-6 { margin-bottom: 1rem !important; }
          .force-mobile .md\\:grid-cols-3 { grid-template-columns: repeat(1, minmax(0, 1fr)) !important; }
          .force-mobile .sm\\:grid-cols-3 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .force-mobile .lg\\:grid-cols-4 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .force-mobile .xl\\:grid-cols-7 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .force-mobile .sm\\:p-4 { padding: 0.75rem !important; }
          .force-mobile .lg\\:p-6 { padding: 0.75rem !important; }
          .force-mobile .sm\\:space-y-3 > * + * { margin-top: 0.5rem !important; }
          .force-mobile .sm\\:rounded-2xl { border-radius: 0.75rem !important; }
          .force-mobile .lg\\:p-4 { padding: 0.75rem !important; }
          .force-mobile .sm\\:w-5 { width: 1rem !important; }
          .force-mobile .sm\\:h-5 { height: 1rem !important; }
          .force-mobile .lg\\:w-6 { width: 1rem !important; }
          .force-mobile .lg\\:h-6 { height: 1rem !important; }
          .force-mobile .sm\\:p-6 { padding: 1rem !important; }
          .force-mobile .lg\\:p-8 { padding: 1rem !important; }
          .force-mobile .sm\\:mb-8 { margin-bottom: 1rem !important; }
          .force-mobile .sm\\:p-3 { padding: 0.75rem !important; }
          .force-mobile .sm\\:mr-4 { margin-right: 0.75rem !important; }
          .force-mobile .sm\\:w-6 { width: 1.25rem !important; }
          .force-mobile .sm\\:h-6 { height: 1.25rem !important; }
          .force-mobile .sm\\:text-xl { font-size: 1.125rem !important; }
          .force-mobile .sm\\:text-base { font-size: 0.875rem !important; }
          .gap-3 { gap: 0.75rem !important; }
          .force-mobile .sm\\:gap-4 { gap: 0.75rem !important; }
          .force-mobile .sm\\:scale-105 { transform: scale(1.02) !important; }
          .force-mobile .sm\\:scale-102 { transform: scale(1.01) !important; }
          .force-mobile .sm\\:hover\\:scale-102:hover { transform: scale(1.01) !important; }
          .force-mobile .sm\\:shadow-xl { box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important; }
          .force-mobile .sm\\:px-6 { padding-left: 1rem !important; padding-right: 1rem !important; }
          .force-mobile .sm\\:py-4 { padding-top: 0.75rem !important; padding-bottom: 0.75rem !important; }
        `}</style>
      )}

      <div className="container mx-auto max-w-7xl px-3 sm:px-4 py-4 sm:py-6">
        {/* Template Mode Indicator - Now handled by sonner toast */}

        {/* Full Coverage Refresh Button */}
        <div className="mb-4 sm:mb-6">
          <motion.button
            onClick={handleFullCoverageRefresh}
            disabled={!canRefresh || isFullRefreshing || isAnalyzing}
            className={`
              w-full rounded-xl px-4 sm:px-6 py-3 sm:py-4 font-medium text-sm sm:text-base 
              transition-all duration-300 transform-gpu
              ${!canRefresh || isFullRefreshing || isAnalyzing
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]'
              }
            `}
            whileTap={(!canRefresh || isFullRefreshing || isAnalyzing) ? {} : { scale: 0.98 }}
            whileHover={(!canRefresh || isFullRefreshing || isAnalyzing) ? {} : { scale: 1.02 }}
          >
            <div className="flex items-center justify-center">
              {isFullRefreshing ? (
                <div className="flex items-center">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"
                  />
                  Processing Full Refresh...
                </div>
              ) : !canRefresh ? (
                <div className="flex items-center">
                  <CircleNotch className="w-4 h-4 mr-2" />
                  Next refresh available in {formatCountdown(refreshCooldown)}
                </div>
              ) : (
                <div className="flex items-center">
                  <ArrowsClockwise className="w-4 h-4 mr-2" />
                  Full Coverage Refresh
                </div>
              )}
            </div>
          </motion.button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6 lg:mb-8">
          <StatCard 
            title={(() => {
              const baseTitle = isTemplateMode ? "AI Readiness" : "AI Coverage";
              if (selectedServiceType) {
                const serviceConfig = getServiceTypeConfig(selectedServiceType);
                return `${baseTitle} for ${serviceConfig.label}`;
              }
              return baseTitle;
            })()}
            value={loading ? "..." : `${stats.coveragePercentage}%`} 
            icon={ChartBar} 
            description={getStatDescription('coverage', isTemplateMode, stats.coveragePercentage, selectedServiceType)}
            isLoading={loading}
            showProgressBar={!loading}
            percentage={stats.coveragePercentage}
          />
          <StatCard 
            title={(() => {
              const baseTitle = isTemplateMode ? "Common" : "Total";
              if (selectedServiceType) {
                const serviceConfig = getServiceTypeConfig(selectedServiceType);
                return `${baseTitle} ${serviceConfig.label} Questions`;
              }
              return `${baseTitle} Questions`;
            })()}
            value={loading ? "..." : stats.total.toLocaleString()} 
            icon={Chat} 
            description={getStatDescription('total', isTemplateMode, stats.total, selectedServiceType)}
            isLoading={loading}
          />
          <StatCard 
            title="Questions We're Trained For" 
            value={loading ? "..." : stats.covered.toLocaleString()} 
            icon={CheckCircle} 
            description={getStatDescription('covered', isTemplateMode, stats.covered, selectedServiceType)}
            isLoading={loading}
          />
        </div>

        {/* Service Type Filter - Only show in template mode */}
        {isTemplateMode && (
          <ServiceTypeFilter
            selectedServiceType={selectedServiceType}
            onServiceTypeChange={handleServiceTypeChange}
            serviceTypeCounts={serviceTypeCounts}
            isLoading={loading}
            availableServiceTypes={accountServiceTypes}
          />
        )}

        {loading && (
          <div className="flex justify-center items-center py-12 sm:py-20">
            <CircleNotch className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400 animate-spin" />
            <p className="ml-3 text-sm sm:text-base text-gray-600">Loading analysis...</p>
          </div>
        )}

        {!loading && filteredCategoriesData.length === 0 && (
          <div className="bg-white p-6 sm:p-8 lg:p-10 rounded-xl shadow-sm border border-gray-100 text-center">
            <Chat className="w-8 h-8 sm:w-10 sm:h-10 text-blue-400 mx-auto mb-3 sm:mb-4"/>
            <h3 className="text-base sm:text-lg font-medium text-gray-700 mb-1 sm:mb-2">
              {selectedServiceType 
                ? `No questions found for ${getServiceTypeConfig(selectedServiceType).label}`
                : isTemplateMode ? "No template data available yet." : "No analysis data available yet."
              }
            </h3>
            <p className="text-xs sm:text-sm text-gray-500">
              {selectedServiceType 
                ? "Try selecting a different service type or 'All Services'"
                : isTemplateMode ? "Template data will be populated soon." : "As calls are analyzed, insights will appear here."
              }
            </p>
          </div>
        )}

        {!loading && filteredCategoriesData.length > 0 && (
          <div className="space-y-2 sm:space-y-3">
            {filteredCategoriesData.map(category => (
              <CategoryAccordionItem 
                key={category.id} 
                category={category}
                expandedItems={expandedItems}
                toggleExpand={toggleExpand}
                onViewCallDetails={handleViewCallDetails}
                onEditSetting={handleEditSetting}
                onCreateSetting={handleCreateSetting}
                onUpdateExisting={handleUpdateExisting}
                availableSettings={availableSettings}
                isTemplateMode={isTemplateMode}
              />
            ))}
          </div>
        )}
      </div>

      {/* Call Details Modal */}
      <CallDetailsModal 
        isOpen={isCallDetailsModalOpen} 
        onClose={() => {
          setIsCallDetailsModalOpen(false);
          setSelectedCallIdForModal(null);
        }} 
        callId={selectedCallIdForModal} 
        supabase={supabase}
        className="z-50"
      />

      {/* Brain Action Selection Modal */}
      <BrainActionModal 
        isOpen={isBrainActionModalOpen} 
        onClose={() => setIsBrainActionModalOpen(false)}
        onActionSelect={handleBrainActionSelect}
      />

      {/* Simple Answer Modal */}
      <SimpleAnswerModal
        isOpen={isSimpleAnswerModalOpen}
        onClose={handleModalClose}
        answer={currentSetting}
      />

      {/* Complex Answer Modal */}
      <ComplexAnswerSettingsModal
        isOpen={isComplexAnswerModalOpen}
        onClose={handleModalClose}
        answer={currentSetting || {}}
        isNew={!currentSetting?.id}
      />

      {/* Simple Action Modal */}
      <CallActionSettingsModal
        isOpen={isSimpleActionModalOpen}
        onClose={handleModalClose}
        action={currentSetting || {}}
        isNew={!currentSetting?.id}
      />

      {/* New Category Modal */}
      <NewCategoryModal 
        isOpen={isNewCategoryModalOpen}
        onClose={handleModalClose}
      />

      {/* Call Your Employee Modal */}
      <AnimatePresence>
        {showCallModal && (
          <motion.div
            className="fixed inset-0 bg-gray-900/20 backdrop-blur-sm flex items-center justify-center z-40 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden"
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-50 p-2 rounded-lg">
                      <Phone className="w-5 h-5 text-blue-500" />
                    </div>
                    <h2 className="text-xl text-gray-900 font-medium">Test Your AI Assistant</h2>
                  </div>
                  <button
                    onClick={() => setShowCallModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-gray-100"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="text-2xl font-medium text-gray-900 text-center">
                    {formatPhoneNumber(accountPhoneNumber)}
                  </div>
                </div>

                <p className="text-sm text-gray-500 mb-6">
                  Call this number to experience exactly what your customers will hear. Test your AI readiness!
                </p>

                <div className="flex justify-end">
                  <button
                    onClick={() => setShowCallModal(false)}
                    className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors duration-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Analysis;